import { useCallback, useMemo, useRef, useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import {
  Bold,
  CircleAlert,
  FileText as FileTextIcon,
  Image as ImageIcon,
  MapPin as MapPinIcon,
  Video as VideoIcon,
  Code,
  Info,
  Italic,
  Megaphone,
  Smile,
  Strikethrough,
} from "lucide-react";
import { ImageDropzone } from "../../../../components/common/ImageDropzone.js";
import { cn } from "../../../../lib/utils.js";
import {
  WHATSAPP_IMAGE_MAX_BYTES,
  isWhatsAppImageMimeAllowed,
} from "@multi-chat/shared";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip/index.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover/index.js";
import {
  applyWhatsAppFormat,
  bodyHasDanglingVariables,
  bodyHasInsufficientVariableDensity,
  DEFAULT_TEMPLATE_SUMMARY_NAME,
  enforceMaxOneHeaderVariable,
  getNextPositionalIndex,
  TEMPLATE_LIMITS,
  templateVariableFormatStatus,
  type WhatsAppFormatAction,
} from "../../template-editor-presentation.js";
import {
  buildFormTemplatePresentation,
  extractPlaceholderKeys,
  pruneStaleExamples,
  type TemplateFormState,
} from "../template-management-form.js";
import { englishTemplateLanguageLabel } from "../template-language-options.js";
import type {
  TemplateCreationCategory,
  TemplateCreationUseCase,
} from "../template-creation-wizard-state.js";
import { MarketingDefaultButtonsEditor } from "./MarketingDefaultButtonsEditor.js";
import { TemplateEditorPreviewPanel } from "./TemplateEditorPreviewPanel.js";
import { WizardRadioSelect } from "./WizardRadioSelect.js";

const ComposerEmojiPicker = lazy(() =>
  import("../../../inbox/ConversationThread/ComposerEmojiPicker.js").then(
    (mod) => ({ default: mod.ComposerEmojiPicker }),
  ),
);

export interface TemplateEditorStepProps {
  category: TemplateCreationCategory;
  useCase: TemplateCreationUseCase;
  formState: TemplateFormState;
  onFormChange: (state: TemplateFormState) => void;
  callingManagerUrl?: string | null;
  /** Create wizard vs edit-from-list. Edit locks Meta-immutable identity fields. */
  mode?: "create" | "edit";
  templateStatusLabel?: string | null;
}

function EditorCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="rounded-lg border border-line bg-surface p-5 md:p-6">
      <div className="mb-5 grid gap-1.5">
        <h3 className="m-0 text-lg font-semibold tracking-tight text-ink">{title}</h3>
        {subtitle ? (
          <p className="m-0 text-[0.8125rem] leading-relaxed text-ink-muted">{subtitle}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function InfoTip({ content }: { content: string }): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-ink-muted hover:text-ink">
          <Info size={14} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-start text-xs whitespace-pre-line">
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

function CounterField({
  value,
  max,
  invalid,
  children,
}: {
  value: string;
  max: number;
  invalid?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="relative">
      {children}
      <span className="pointer-events-none absolute end-3 top-2 flex items-center gap-1 text-xs text-ink-muted">
        {invalid ? <CircleAlert size={14} className="text-error" /> : null}
        <span>
          {value.length}/{max}
        </span>
      </span>
    </div>
  );
}

export function TemplateEditorStep({
  category,
  useCase,
  formState,
  onFormChange,
  callingManagerUrl = null,
  mode = "create",
  templateStatusLabel = null,
}: TemplateEditorStepProps): React.JSX.Element {
  const { t } = useTranslation();
  const isEditMode = mode === "edit";
  const [imageError, setImageError] = useState<string | null>(null);
  const [sampleChoice, setSampleChoice] = useState<"none" | "text" | "media">(() =>
    formState.headerKind === "IMAGE" ? "media" : "text",
  );
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const patch = useCallback(
    (partial: Partial<TemplateFormState>) => {
      onFormChange(pruneStaleExamples({ ...formState, ...partial }));
    },
    [formState, onFormChange],
  );

  const presentation = useMemo(
    () => buildFormTemplatePresentation(formState),
    [formState],
  );

  const languageLabel = englishTemplateLanguageLabel(formState.language);
  const categoryLabel = t(`whatsapp.templates.manage.wizard.categories.${category}`);
  const useCaseLabel = t(`whatsapp.templates.manage.wizard.useCases.${useCase}.title`);
  const summaryName = formState.name || DEFAULT_TEMPLATE_SUMMARY_NAME;

  const headerKeys =
    formState.headerKind === "TEXT" && formState.hasHeader
      ? extractPlaceholderKeys(formState.headerText)
      : [];
  const bodyKeys = extractPlaceholderKeys(formState.bodyText);
  const headerHasVariable = headerKeys.length > 0;
  const headerVarStatus = templateVariableFormatStatus(
    formState.headerText,
    formState.parameterFormat,
  );
  const bodyVarStatus = templateVariableFormatStatus(
    formState.bodyText,
    formState.parameterFormat,
  );
  const danglingBody = bodyHasDanglingVariables(formState.bodyText);
  const denseBodyInvalid = bodyHasInsufficientVariableDensity(formState.bodyText);
  const headerTooMany = headerKeys.length > 1;

  function insertHeaderVariable(): void {
    if (headerHasVariable) return;
    const token = formState.parameterFormat === "NAMED" ? "{{header_text}}" : "{{1}}";
    patch({
      hasHeader: true,
      headerKind: "TEXT",
      headerText: `${formState.headerText}${token}`,
    });
  }

  function insertBodyVariable(): void {
    const el = bodyRef.current;
    const text = formState.bodyText;
    const insertPos = el?.selectionStart ?? text.length;
    const token =
      formState.parameterFormat === "NAMED"
        ? `{{variable_${extractPlaceholderKeys(text).length + 1}}}`
        : `{{${getNextPositionalIndex(text)}}}`;
    const next = `${text.slice(0, insertPos)}${token}${text.slice(insertPos)}`;
    patch({ bodyText: next });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = insertPos + token.length;
      el.setSelectionRange(caret, caret);
    });
  }

  function applyFormat(action: WhatsAppFormatAction): void {
    const el = bodyRef.current;
    if (!el) return;
    const result = applyWhatsAppFormat(el.value, el.selectionStart, el.selectionEnd, action);
    patch({ bodyText: result.value });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  }

  function insertEmoji(emoji: string): void {
    const el = bodyRef.current;
    const text = formState.bodyText;
    const insertPos = el?.selectionStart ?? text.length;
    const next = `${text.slice(0, insertPos)}${emoji}${text.slice(insertPos)}`;
    patch({ bodyText: next });
  }

  const formatActions: { action: WhatsAppFormatAction; icon: typeof Bold; label: string }[] = [
    { action: "monospace", icon: Code, label: t("whatsapp.templates.manage.wizard.editor.formatting.monospace") },
    { action: "strikethrough", icon: Strikethrough, label: t("whatsapp.templates.manage.wizard.editor.formatting.strikethrough") },
    { action: "italic", icon: Italic, label: t("whatsapp.templates.manage.wizard.editor.formatting.italic") },
    { action: "bold", icon: Bold, label: t("whatsapp.templates.manage.wizard.editor.formatting.bold") },
  ];

  const previewPanel = (
    <TemplateEditorPreviewPanel
      presentation={presentation}
      contentLanguage={formState.language}
    />
  );

  return (
    <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(18.5rem,24rem)] lg:gap-6">
      <div className="grid min-w-0 gap-5">
        <div className="flex items-start gap-3 rounded-lg border border-line bg-surface px-5 py-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
            <Megaphone size={18} aria-hidden="true" />
          </div>
          <div className="min-w-0 grid gap-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span dir="ltr" className="font-semibold text-ink [unicode-bidi:isolate]">
                {summaryName}
              </span>
              <span className="text-ink-muted">·</span>
              <span className="text-ink-muted">{languageLabel}</span>
            </div>
            <div className="text-xs text-ink-subtle">
              {categoryLabel} · {useCaseLabel}
              {templateStatusLabel ? ` · ${templateStatusLabel}` : ""}
            </div>
          </div>
        </div>

        <EditorCard
          title={t("whatsapp.templates.manage.wizard.editor.contentTitle")}
          subtitle={t("whatsapp.templates.manage.wizard.editor.contentSubtitle")}
        >
          <div className="grid gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-ink">
                    {t("whatsapp.templates.manage.wizard.editor.variableType")}
                  </span>
                  <InfoTip content={t("whatsapp.templates.manage.wizard.editor.variableTypeTooltip")} />
                </div>
                <WizardRadioSelect
                  ariaLabel={t("whatsapp.templates.manage.wizard.editor.variableType")}
                  value={formState.parameterFormat}
                  onChange={(value) =>
                    patch({ parameterFormat: value as TemplateFormState["parameterFormat"] })
                  }
                  disabled={isEditMode}
                  options={[
                    { value: "NAMED", label: t("whatsapp.templates.manage.wizard.editor.variableNamed") },
                    { value: "POSITIONAL", label: t("whatsapp.templates.manage.wizard.editor.variablePositional") },
                  ]}
                />
                {isEditMode ? (
                  <p className="m-0 text-xs text-ink-muted">
                    {t("whatsapp.templates.manage.fields.parameterFormatLocked")}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t("whatsapp.templates.manage.wizard.editor.mediaSample")}
                </span>
                <div
                  className="inline-flex w-fit gap-1 rounded-lg border border-line bg-surface-muted p-1"
                  role="radiogroup"
                  aria-label={t("whatsapp.templates.manage.wizard.editor.mediaSample")}
                >
                  {(
                    [
                      { value: "none", label: t("whatsapp.templates.manage.wizard.editor.mediaNone") },
                      { value: "text", label: t("whatsapp.templates.manage.wizard.editor.mediaText") },
                      { value: "media", label: t("whatsapp.templates.manage.wizard.editor.mediaMedia") },
                    ] as const
                  ).map((option) => {
                    const selected = sampleChoice === option.value;
                    const disabled =
                      isEditMode && formState.headerKind === "IMAGE" && option.value !== "media";
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={disabled}
                        className={cn(
                          "rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                          selected
                            ? "bg-surface text-ink shadow-sm"
                            : "text-ink-muted hover:text-ink",
                          disabled && "cursor-not-allowed opacity-50",
                        )}
                        onClick={() => {
                          if (disabled || option.value === sampleChoice) return;
                          if (formState.headerImagePreviewUrl?.startsWith("blob:")) {
                            URL.revokeObjectURL(formState.headerImagePreviewUrl);
                          }
                          setSampleChoice(option.value);
                          if (option.value === "media") {
                            patch({ headerKind: "IMAGE", hasHeader: true, headerText: "" });
                            return;
                          }
                          if (option.value === "text") {
                            patch({
                              headerKind: formState.headerText.trim() ? "TEXT" : "NONE",
                              hasHeader: formState.headerText.trim().length > 0,
                              headerImageFile: null,
                              headerImagePreviewUrl: null,
                              hasStoredHeaderImage: false,
                            });
                            return;
                          }
                          patch({
                            headerKind: "NONE",
                            hasHeader: false,
                            headerText: "",
                            headerImageFile: null,
                            headerImagePreviewUrl: null,
                            hasStoredHeaderImage: false,
                          });
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {sampleChoice === "media" ? (
              <div
                className="grid grid-cols-2 gap-2 sm:grid-cols-4"
                role="radiogroup"
                aria-label={t("whatsapp.templates.manage.wizard.editor.mediaType")}
              >
                {(
                  [
                    { value: "image", icon: ImageIcon, label: t("whatsapp.templates.manage.wizard.editor.mediaImage"), enabled: true },
                    { value: "video", icon: VideoIcon, label: t("whatsapp.templates.manage.wizard.editor.mediaVideo"), enabled: false },
                    { value: "document", icon: FileTextIcon, label: t("whatsapp.templates.manage.wizard.editor.mediaDocument"), enabled: false },
                    { value: "location", icon: MapPinIcon, label: t("whatsapp.templates.manage.wizard.editor.mediaLocation"), enabled: false },
                  ] as const
                ).map((option) => {
                  const Icon = option.icon;
                  const selected = option.enabled;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={!option.enabled}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-lg border px-2 py-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                        selected
                          ? "border-accent bg-accent-soft text-ink"
                          : "cursor-not-allowed border-dashed border-line bg-surface text-ink-subtle",
                      )}
                    >
                      <Icon size={18} aria-hidden="true" className={selected ? "text-accent" : undefined} />
                      <span>{option.label}</span>
                      {!option.enabled ? (
                        <span className="rounded-full bg-surface-muted px-1.5 py-px text-[0.625rem] text-ink-muted">
                          {t("whatsapp.templates.manage.wizard.editor.mediaComingSoon")}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : null}

            {sampleChoice === "media" ? (
              <div className="grid gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t("whatsapp.templates.manage.wizard.editor.headerImage")}
                </span>
                <ImageDropzone
                  previewUrl={formState.headerImagePreviewUrl}
                  fileName={formState.headerImageFile?.name ?? null}
                  byteSize={formState.headerImageFile?.size ?? null}
                  accept="image/jpeg,image/jpg,image/png"
                  selectLabel={t("whatsapp.templates.manage.wizard.editor.imageSelect")}
                  changeLabel={t("whatsapp.templates.manage.wizard.editor.imageChange")}
                  removeLabel={t("whatsapp.templates.manage.wizard.editor.imageRemove")}
                  dropLabel={t("whatsapp.templates.manage.wizard.editor.imageDrop")}
                  emptyHint={t("whatsapp.templates.manage.wizard.editor.imageHint")}
                  error={imageError}
                  onSelectFile={(file) => {
                    if (file.size > WHATSAPP_IMAGE_MAX_BYTES) {
                      setImageError(t("whatsapp.templates.manage.wizard.editor.imageTooLarge"));
                      return;
                    }
                    if (file.type && !isWhatsAppImageMimeAllowed(file.type)) {
                      setImageError(t("whatsapp.templates.manage.wizard.editor.imageInvalidType"));
                      return;
                    }
                    if (formState.headerImagePreviewUrl?.startsWith("blob:")) {
                      URL.revokeObjectURL(formState.headerImagePreviewUrl);
                    }
                    setImageError(null);
                    patch({
                      headerKind: "IMAGE",
                      hasHeader: true,
                      headerImageFile: file,
                      headerImagePreviewUrl: URL.createObjectURL(file),
                    });
                  }}
                  onRemove={
                    isEditMode && formState.hasStoredHeaderImage && !formState.headerImageFile
                      ? undefined
                      : () => {
                          if (formState.headerImagePreviewUrl?.startsWith("blob:")) {
                            URL.revokeObjectURL(formState.headerImagePreviewUrl);
                          }
                          setImageError(null);
                          patch({
                            headerImageFile: null,
                            headerImagePreviewUrl: formState.hasStoredHeaderImage
                              ? formState.headerImagePreviewUrl
                              : null,
                          });
                        }
                  }
                />
              </div>
            ) : null}

            {sampleChoice === "text" ? (
            <div className="grid gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t("whatsapp.templates.manage.wizard.editor.headerLabel")}
              </span>
              <CounterField
                value={formState.headerText}
                max={TEMPLATE_LIMITS.headerText}
                invalid={
                  headerTooMany ||
                  headerVarStatus === "named_mismatch" ||
                  headerVarStatus === "positional_mismatch"
                }
              >
                <input
                  className="h-10 w-full rounded-md border border-line bg-surface px-3 pe-14 text-sm text-ink focus:border-accent"
                  value={formState.headerText}
                  maxLength={TEMPLATE_LIMITS.headerText}
                  placeholder={t("whatsapp.templates.manage.wizard.editor.headerPlaceholder", {
                    language: languageLabel,
                  })}
                  onChange={(event) => {
                    const headerText = enforceMaxOneHeaderVariable(event.target.value);
                    patch({
                      hasHeader: headerText.length > 0,
                      headerKind: headerText.length > 0 ? "TEXT" : "NONE",
                      headerText,
                    });
                  }}
                />
              </CounterField>
              <div dir="ltr" className="flex items-center justify-start gap-2">
                <InfoTip content={t("whatsapp.templates.manage.wizard.editor.headerVariableTooltip")} />
                <button
                  type="button"
                  className="text-xs font-medium text-accent hover:underline disabled:opacity-40 disabled:no-underline"
                  onClick={insertHeaderVariable}
                  disabled={headerHasVariable}
                >
                  + {t("whatsapp.templates.manage.wizard.editor.addVariable")}
                </button>
              </div>
              {headerTooMany ? (
                <span className="text-xs text-error">
                  {t("whatsapp.templates.manage.wizard.editor.headerTooManyVariables")}
                </span>
              ) : null}
              {headerVarStatus === "named_mismatch" ? (
                <span className="text-xs text-error">
                  {t("whatsapp.templates.manage.wizard.editor.variableNamedMismatch")}
                </span>
              ) : null}
              {headerVarStatus === "positional_mismatch" ? (
                <span className="text-xs text-error">
                  {t("whatsapp.templates.manage.wizard.editor.variablePositionalMismatch")}
                </span>
              ) : null}
            </div>
            ) : null}

            <div className="grid gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t("whatsapp.templates.manage.wizard.editor.bodyLabel")}
              </span>
              <div
                className={cn(
                  "overflow-hidden rounded-md border focus-within:border-accent",
                  danglingBody ||
                    denseBodyInvalid ||
                    bodyVarStatus === "named_mismatch" ||
                    bodyVarStatus === "positional_mismatch"
                    ? "border-error"
                    : "border-line",
                )}
              >
                <CounterField
                  value={formState.bodyText}
                  max={TEMPLATE_LIMITS.bodyText}
                  invalid={danglingBody || denseBodyInvalid}
                >
                  <textarea
                    ref={bodyRef}
                    className="min-h-[10rem] w-full resize-y border-0 bg-surface px-3.5 py-3 pe-16 text-sm leading-relaxed text-ink focus:outline-none"
                    value={formState.bodyText}
                    maxLength={TEMPLATE_LIMITS.bodyText}
                    placeholder={t("whatsapp.templates.manage.wizard.editor.bodyPlaceholder", {
                      language: languageLabel,
                    })}
                    onChange={(event) => patch({ bodyText: event.target.value })}
                  />
                </CounterField>
                {danglingBody ? (
                  <p className="m-0 border-t border-error/20 px-3 py-2 text-xs text-error">
                    {t("whatsapp.templates.manage.wizard.editor.bodyDanglingVariables")}
                  </p>
                ) : null}
                {!danglingBody && denseBodyInvalid ? (
                  <p className="m-0 border-t border-error/20 px-3 py-2 text-xs text-error">
                    {t("whatsapp.templates.manage.wizard.editor.bodyVariableDensity")}
                  </p>
                ) : null}
                {bodyVarStatus === "named_mismatch" ? (
                  <p className="m-0 border-t border-error/20 px-3 py-2 text-xs text-error">
                    {t("whatsapp.templates.manage.wizard.editor.variableNamedMismatch")}
                  </p>
                ) : null}
                {bodyVarStatus === "positional_mismatch" ? (
                  <p className="m-0 border-t border-error/20 px-3 py-2 text-xs text-error">
                    {t("whatsapp.templates.manage.wizard.editor.variablePositionalMismatch")}
                  </p>
                ) : null}
                <div
                  dir="ltr"
                  className="flex flex-wrap items-center justify-start gap-0.5 border-t border-line px-1 py-1"
                >
                  <InfoTip content={t("whatsapp.templates.manage.wizard.editor.headerVariableTooltip")} />
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs font-medium text-accent hover:bg-accent/5"
                    onClick={insertBodyVariable}
                  >
                    + {t("whatsapp.templates.manage.wizard.editor.addVariable")}
                  </button>
                  {formatActions.map(({ action, icon: Icon, label }) => (
                    <Tooltip key={action}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="rounded p-1.5 text-ink-muted hover:bg-surface-muted hover:text-ink"
                          onClick={() => applyFormat(action)}
                          aria-label={label}
                        >
                          <Icon size={14} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {label}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="rounded p-1.5 text-ink-muted hover:bg-surface-muted hover:text-ink"
                        aria-label="Emoji"
                      >
                        <Smile size={14} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[min(20rem,calc(100vw-2rem))] p-0">
                      <Suspense fallback={null}>
                        <ComposerEmojiPicker onSelect={insertEmoji} />
                      </Suspense>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {headerKeys.length > 0 || bodyKeys.length > 0 ? (
              <div className="grid gap-3 rounded-md border border-line bg-surface-muted/50 p-4">
                <div className="grid gap-1">
                  <span className="text-sm font-semibold text-ink">
                    {t("whatsapp.templates.manage.wizard.editor.variableExamplesTitle")}
                  </span>
                  <span className="text-[0.8rem] leading-relaxed text-ink-muted">
                    {t("whatsapp.templates.manage.wizard.editor.variableExamplesSubtitle")}
                  </span>
                </div>
                {headerKeys.map((key) => {
                  const token = `{{${key}}}`;
                  const empty = !formState.headerExample.trim();
                  return (
                    <div key={`h-${key}`} className="grid gap-1.5">
                      <span className="text-xs font-medium text-ink-muted">
                        {t("whatsapp.templates.manage.wizard.editor.variableGroupHeader")}
                      </span>
                      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2">
                        <span
                          dir="ltr"
                          className="rounded border border-line bg-surface px-2 py-2 text-xs text-ink-muted [unicode-bidi:isolate]"
                        >
                          {token}
                        </span>
                        <div className="relative">
                          <input
                            className={cn(
                              "h-10 w-full rounded-md border bg-surface px-3 pe-8 text-sm",
                              empty ? "border-error" : "border-line",
                            )}
                            value={formState.headerExample}
                            placeholder={t(
                              "whatsapp.templates.manage.wizard.editor.variableExamplePlaceholder",
                              { token },
                            )}
                            onChange={(event) =>
                              onFormChange({ ...formState, headerExample: event.target.value })
                            }
                          />
                          {empty ? (
                            <CircleAlert
                              size={14}
                              className="absolute end-3 top-1/2 -translate-y-1/2 text-error"
                            />
                          ) : null}
                        </div>
                      </div>
                      {empty ? (
                        <span className="text-xs text-error">
                          {t("whatsapp.templates.manage.wizard.editor.variableExampleError")}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
                {bodyKeys.map((key) => {
                  const token = `{{${key}}}`;
                  const empty = !(formState.bodyExamples[key] ?? "").trim();
                  return (
                    <div key={`b-${key}`} className="grid gap-1.5">
                      <span className="text-xs font-medium text-ink-muted">
                        {t("whatsapp.templates.manage.wizard.editor.variableGroupBody")}
                      </span>
                      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2">
                        <span
                          dir="ltr"
                          className="rounded border border-line bg-surface px-2 py-2 text-xs text-ink-muted [unicode-bidi:isolate]"
                        >
                          {token}
                        </span>
                        <div className="relative">
                          <input
                            className={cn(
                              "h-10 w-full rounded-md border bg-surface px-3 pe-8 text-sm",
                              empty ? "border-error" : "border-line",
                            )}
                            value={formState.bodyExamples[key] ?? ""}
                            placeholder={t(
                              "whatsapp.templates.manage.wizard.editor.variableExamplePlaceholder",
                              { token },
                            )}
                            onChange={(event) =>
                              onFormChange({
                                ...formState,
                                bodyExamples: {
                                  ...formState.bodyExamples,
                                  [key]: event.target.value,
                                },
                              })
                            }
                          />
                          {empty ? (
                            <CircleAlert
                              size={14}
                              className="absolute end-3 top-1/2 -translate-y-1/2 text-error"
                            />
                          ) : null}
                        </div>
                      </div>
                      {empty ? (
                        <span className="text-xs text-error">
                          {t("whatsapp.templates.manage.wizard.editor.variableExampleError")}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            <div className="grid gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t("whatsapp.templates.manage.wizard.editor.footerLabel")}
              </span>
              <CounterField value={formState.footerText} max={TEMPLATE_LIMITS.footerText}>
                <input
                  className="h-10 w-full rounded-md border border-line bg-surface px-3 pe-14 text-sm text-ink focus:border-accent"
                  value={formState.footerText}
                  maxLength={TEMPLATE_LIMITS.footerText}
                  placeholder={t("whatsapp.templates.manage.wizard.editor.footerPlaceholder", {
                    language: languageLabel,
                  })}
                  onChange={(event) =>
                    patch({ hasFooter: event.target.value.length > 0, footerText: event.target.value })
                  }
                />
              </CounterField>
            </div>
          </div>
        </EditorCard>

        <EditorCard
          title={`${t("whatsapp.templates.manage.wizard.editor.buttonsTitle")} • ${t("whatsapp.templates.manage.wizard.editor.optional")}`}
          subtitle={t("whatsapp.templates.manage.wizard.editor.buttonsSubtitle")}
        >
          <MarketingDefaultButtonsEditor
            formState={formState}
            onChange={(buttons) => patch({ buttons })}
            callingManagerUrl={callingManagerUrl}
          />
        </EditorCard>

        <div className="lg:hidden">{previewPanel}</div>
      </div>

      <aside className="hidden lg:block sticky top-0 self-start">{previewPanel}</aside>
    </div>
  );
}
